import { TemplateAPI, type Node } from "@livereader/graphly-d3";
import { recolorGlyphSvg } from "./useItemizerComposite";

// Factory for every Graph Builder node template: a rounded polygon outline
// with the node title centred and the node's glyph rendered above the shape.
//
// Polygon(n, 180, 1) draws its first vertex at 12 o'clock inside a 360x360
// box, so orientation is corrected per shape via `rotation`:
//   circle   usePolygonShape(30)        (30 sides ~ circle, matches old useCircle)
//   triangle usePolygonShape(3)         (point up)
//   square   usePolygonShape(4, 45)     (flat top)
//   diamond  usePolygonShape(4)         (point up)
//   pentagon usePolygonShape(5)         (point up)
//   hexagon  usePolygonShape(6, 30)     (flat top, matches old useHexagon)
//   octagon  usePolygonShape(8, 22.5)   (flat top, stop-sign)
export const usePolygonShape = (sides: number, rotation = 0) => {
	// Glyph box: centred above the polygon's 360x360 box with a small gap.
	const badgeSize = 120;
	const badgeX = (360 - badgeSize) / 2;
	const badgeY = -(badgeSize + 12);

	return {
		// 360 wide x ~492 tall including the glyph zone; 190 keeps the polygon
		// itself at the same on-screen size the old 140/360 templates had.
		shapeSize: 190,
		shapeBuilder: shapeBuilder,
		// ajv schema for node.payload; graphly warns without one.
		shapePayload: {
			type: 'object',
			properties: {
				title: { type: 'string' },
				color: { type: 'string' },
				glyphColor: { type: 'string' },
				svg: { type: 'string' },
				x: { type: 'number' },
				y: { type: 'number' },
			},
		},
	};

	function shapeBuilder(data: Node, Template: any) {
		const { Shape, SVGShape, TextCollection, CollectionStyle, ShapeStyle, Alignment, OnZoom, LODStyle } = Template;
		const color = data.payload?.color ?? '#51ff00';
		const shape = TemplateAPI.Shape.Polygon(sides, 180, 1);
		shape.select('path')
			.style('stroke', color)
			.style('fill', 'none')
			.style('stroke-width', '8')
			.attr('transform', `translate(180,180) rotate(${rotation})`);
		shape.classed("gly_teal_fill", false);
		shape.classed("gly-selectable", true);

		const bbox = Shape.getBBox(shape);
		const titleShape = TextCollection(
			data.payload?.title ?? '',
			CollectionStyle(100, bbox.width * 0.8, bbox.width * (data.payload?.x ?? 0.10), bbox.height * (data.payload?.y ?? 0.50), 10, 10, 2, Alignment.Center),
			[ShapeStyle('class', 'gly_text.light'), ShapeStyle('font-size', '4em')]
		);
		shape.append(() => titleShape.node());

		// Invisible spacer reserves the glyph zone whether or not a glyph is
		// set, so every node renders at the same scale (graphly scales by the
		// template's bounding box).
		const spacer = SVGShape(
			`<rect x="${badgeX}" y="${badgeY}" width="${badgeSize}" height="${badgeSize}" fill="none" stroke="none" />`
		);
		shape.append(() => spacer.node());

		// Glyph above the shape, recoloured to the node colour. recolorGlyphSvg
		// normalises paint and drops any background fill.
		if (data.payload?.svg) {
			const glyphColor = data.payload?.glyphColor ?? color;
			const glyphSvg = recolorGlyphSvg(data.payload.svg, {
				fill: glyphColor,
				stroke: 'transparent',
				background: 'transparent',
			});
			const badge = SVGShape(glyphSvg);
			// A nested <svg> without explicit size fills the whole canvas —
			// pin it to its 512 viewBox so the scale() below lands at badgeSize.
			badge.select('svg').attr('width', 512).attr('height', 512);
			badge.attr('transform', `translate(${badgeX}, ${badgeY}) scale(${badgeSize / 512})`);
			shape.append(() => badge.node());
		}

		OnZoom(data, [0.6], [LODStyle(titleShape, 'class', 'hidden', (k: any) => k < 0.2)]);
		return shape;
	}
};
